class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.predictive-search-item');
    this.form = this.querySelector('form');
    this.abortController = null;
    this.cache = new Map();
    this.lastTerm = '';
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', this.debounce(() => this.onChange(), 180));
    this.input.addEventListener('focus', () => {
      const term = this.input.value.trim();
      if (term.length >= 2 && this.results.innerHTML.trim()) this.open();
    });
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
    this.results.addEventListener('keydown', (event) => this.onResultsKeydown(event));
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  normalizeTerm(term) {
    if (window.BPBSearchIntent && typeof window.BPBSearchIntent.normalize === 'function') {
      return window.BPBSearchIntent.normalize(term) || term;
    }
    return term;
  }

  onChange() {
    const term = this.input.value.trim();
    this.lastTerm = term;
    if (term.length < 2) {
      this.close(true);
      return;
    }
    this.getSearchResults(term);
  }

  getSearchResults(term) {
    const searchTerm = this.normalizeTerm(term);
    const key = searchTerm.toLowerCase();
    if (this.cache.has(key)) {
      this.render(this.cache.get(key), term, searchTerm);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = root.endsWith('/') ? root : root + '/';
    const url = base + 'search/suggest?q=' + encodeURIComponent(searchTerm) + '&resources[type]=product&resources[limit]=6&section_id=predictive-search';

    fetch(url, { signal: this.abortController.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';
        this.cache.set(key, markup);
        this.render(markup, term, searchTerm);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') this.close();
      })
      .finally(() => this.setLoading(false));
  }

  render(markup, rawTerm, searchTerm) {
    if (this.input.value.trim() !== rawTerm) return;
    this.results.innerHTML = markup;
    if (rawTerm.toLowerCase() !== searchTerm.toLowerCase()) {
      this.results.querySelectorAll('a[href*="/search"]').forEach((link) => {
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('bpb_original_q', rawTerm);
          link.href = url.pathname + url.search + url.hash;
        } catch (error) {}
      });
    }
    this.open();
  }

  setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    this.results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (isLoading && !this.results.innerHTML.trim()) {
      this.results.innerHTML = '<div class="bpb-live-search__loading"><span></span>Searching pens…</div>';
      this.open();
    }
  }

  open() {
    document.querySelectorAll('predictive-search[search-open]').forEach((element) => {
      if (element !== this && typeof element.close === 'function') element.close();
    });
    this.setAttribute('search-open', '');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close(clearResults) {
    this.removeAttribute('search-open');
    this.input.setAttribute('aria-expanded', 'false');
    if (clearResults) this.results.innerHTML = '';
  }

  getOptions() {
    return Array.from(this.results.querySelectorAll('[data-bpb-live-option]'));
  }

  onInputKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'ArrowDown' && this.hasAttribute('search-open')) {
      const options = this.getOptions();
      if (options.length) {
        event.preventDefault();
        options[0].focus();
      }
    }
  }

  onResultsKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = this.getOptions();
    const current = options.indexOf(document.activeElement);
    if (current < 0) return;
    event.preventDefault();
    const next = event.key === 'ArrowDown' ? Math.min(current + 1, options.length - 1) : Math.max(current - 1, 0);
    options[next].focus();
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}